import React, { useEffect, useRef, useState } from "react";
import { Button, Icon, PlainButton } from "@culturehq/components";
import { Story, Template } from "chq";

import { useProfile } from "app/context";
import useLatestMedia from "app/hooks/useLatestMedia";
import { Trans } from "app/utils";

import { TimeDiff } from "components/common/Time";
import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";
import VideoStudioContext from "components/stories/video-studio/contexts/VideoStudioContext";

import { TemplateType, useTemplateBuilder } from "./context/TemplateBuilderContext";
import styles from "./buildertemplates.module.scss";

interface BuilderTemplateRowProps {
  template: Template;
}

const BuilderTemplateRow = ({ template }: BuilderTemplateRowProps) => {
  const dropdownRef = useRef<HTMLTableCellElement | null>(null);
  const { profile } = useProfile();
  const [isDragging, setIsDragging] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { name, type, url, posterUrl, createdAt, status, usageCount } = template;

  const mediaUrl = posterUrl || url;
  const {
    isFetching: mediaIsFetching,
    media,
    thirdPartyMedias,
    lastMediaVersion,
    onThirdPartyMediasReload,
    onUpdateDraftVersion
  } = useLatestMedia({ media: { id: template.mediaId } }, profile, {
    isTemplateBuilder: true,
    creatomateJson: template.creatomateJson
  } as any);
  const story = { media };
  const { startTemplateBuilder, handleUpdate, handleDelete } = useTemplateBuilder();
  const isLastMediaVersionProcessing = (lastMediaVersion as any)?.status === "processing";

  const handleDownload = () => handleUpdate({ ...template, download: true });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  return (
    <tr>
      <td
        className={`sortable__drag-handler ${isDragging ? "active" : ""}`}
        onMouseDown={() => setIsDragging(true)}
        onMouseUp={() => setIsDragging(false)}
      />
      <td className={styles["builder-template-row__td--description"]}>
        <img className="StoryTable-Details__Image" src={mediaUrl} alt="" />
        <Trans><span style={{ marginLeft: "5px" }}>{name}</span></Trans>
      </td>
      <td className={styles["builder-template-row__td"]}>
        <Trans>{type}</Trans>
      </td>
      <td className={styles["builder-template-row__td"]}>
        <TimeDiff time={createdAt as string} />
      </td>
      <td className={styles["builder-template-row__td"]}>
        <Trans>{status}</Trans>
      </td>
      <td className={styles["builder-template-row__td--usage"]}>
        <Trans>{usageCount}</Trans>
      </td>
      <td ref={dropdownRef} className={styles["builder-template__actions-column"]}>
        {mediaIsFetching ? (
          <Button
            className={styles["builder-template__edit-button"]}
            icon="edit"
            disabled
          >
            <Trans>Edit</Trans>
          </Button>
        ) : (
          <VideoStudioContext
            story={story as Story}
            mediaVersionHistory={thirdPartyMedias}
            thirdPartyMediaIsFetching={mediaIsFetching}
            isLastMediaVersionProcessing={isLastMediaVersionProcessing}
            lastMediaVersion={lastMediaVersion}
            onThirdPartyMediasReload={onThirdPartyMediasReload}
            onUpdateDraftVersion={onUpdateDraftVersion}
            templateId={template.value}
            CustomButton={
              ({ onClick }: any) => (
                <Button
                  className={styles["builder-template__edit-button"]}
                  icon="edit"
                  onClick={() => {
                    startTemplateBuilder(template.type as TemplateType, template);
                    onClick();
                  }}
                >
                  <Trans>Edit</Trans>
                </Button>
              )
            }
          />
        )}

        <PlainButton
          className={styles["dropdown-trigger"]}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <Icon icon="close" /> : <Icon icon="kabob" />}
        </PlainButton>

        <ChqModalConfirmDelete
          onAccept={() => handleDelete(template)}
          trigger={(onTrigger: any) => (
            isMenuOpen && (
              <div className={styles["dropdown-menu"]}>
                <li className={styles["dropdown-item"]} onClick={handleDownload}>
                  <Icon icon="download" />
                  <Trans>Download</Trans>
                </li>
                <li className={styles["dropdown-item"]} onClick={onTrigger}>
                  <Icon icon="trash-a-outlined" />
                  <Trans>Delete</Trans>
                </li>
              </div>
            )
          )}
        >
          <Trans>Are you sure you want to delete this template?</Trans>
        </ChqModalConfirmDelete>
      </td>
    </tr>
  );
};

export default BuilderTemplateRow;
